import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { HeartIcon as Heart } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { isNativeApp } from "@/lib/nativeBridge";
import { haptic } from "@/lib/native";
import IapUpgradeButton from "@/components/IapUpgradeButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

/**
 * Watch/unwatch toggle for Buyer-Pass holders.
 *
 * Renders a heart button. Behavior:
 *   - No Buyer Pass → renders as a disabled-looking CTA that opens an
 *     inline "Buyer Pass required" hint and links to /billing.
 *   - Active Pass → toggles the watch status optimistically and syncs
 *     with the backend.
 *
 * Props:
 *   targetType: "breeder" | "litter"
 *   targetId:   string
 *   size:       optional pixel size for the icon (default 20)
 *   className:  extra classes for the button
 */
export default function WatchButton({ targetType, targetId, size = 20, className = "" }) {
  const [watching, setWatching] = useState(false);
  const [hasPass, setHasPass] = useState(null); // null = loading
  const [busy, setBusy] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);

  useEffect(() => {
    if (!targetId) return;
    let cancelled = false;
    Promise.all([
      api.get("/me/buyer-pass").then((r) => r.data?.active).catch(() => false),
      api
        .get(`/me/watchlist/status/${targetType}/${targetId}`)
        .then((r) => r.data?.watching)
        .catch(() => false),
    ]).then(([pass, w]) => {
      if (cancelled) return;
      setHasPass(!!pass);
      setWatching(!!w);
    });
    return () => { cancelled = true; };
  }, [targetType, targetId]);

  const toggle = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (busy) return;
    if (!hasPass) {
      if (isNativeApp()) {
        toast.info("Watching is included with an active buyer plan.", {
          action: { label: "My account", onClick: () => { window.location.href = "/billing"; } },
        });
      } else {
        toast.info("Buyer Pass unlocks the Watching list.", {
          action: { label: "See pricing", onClick: () => { window.location.href = "/billing"; } },
        });
      }
      return;
    }
    setBusy(true);
    const next = !watching;
    setWatching(next); // optimistic
    try {
      if (next) {
        await api.post("/me/watchlist", { target_type: targetType, target_id: targetId });
        haptic("light");
        toast.success("Added to your Watching list");
      } else {
        await api.delete(`/me/watchlist/${targetType}/${targetId}`);
        toast.success("Removed from your Watching list");
      }
    } catch (err) {
      setWatching(!next); // rollback
      toast.error(err.response?.data?.detail || "Couldn't update your list.");
    } finally {
      setBusy(false);
    }
  };

  const base =
    "inline-flex items-center justify-center rounded-full border transition-colors h-9 w-9";
  const activeStyle = watching
    ? "bg-[#C9A227]/15 border-[#C9A227]/50 text-[#C9A227]"
    : "bg-white border-[#E5E5E5] text-[#6B6B6B] hover:text-[#C9A227] hover:border-[#C9A227]/40";
  const lockedStyle = "bg-white border-[#E5E5E5] text-[#9A9A9A] hover:text-[#C9A227]";

  // Logged-out or still loading: render a neutral placeholder link to
  // /billing so the UI shape never jumps.
  if (hasPass === null) {
    return (
      <span
        className={`${base} ${lockedStyle} opacity-60 ${className}`}
        data-testid={`watch-btn-${targetType}-${targetId}-loading`}
      >
        <Heart size={size} weight="duotone" />
      </span>
    );
  }

  if (!hasPass) {
    return (
      <>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); setPaywallOpen(true); }}
          className={`${base} ${lockedStyle} ${className}`}
          title="Buyer Pass unlocks the Watching list"
          data-testid={`watch-btn-${targetType}-${targetId}-locked`}
        >
          <Heart size={size} weight="duotone" />
        </button>
        <Dialog open={paywallOpen} onOpenChange={setPaywallOpen}>
          <DialogContent className="max-w-md" data-testid="watch-paywall-dialog" onClick={(e) => e.stopPropagation()}>
            <DialogHeader>
              <DialogTitle className="font-spectral text-2xl flex items-center gap-2">
                <Heart size={20} weight="fill" className="text-[#C1272D]" />
                Watch this {targetType === "litter" ? "litter" : "kennel"}
              </DialogTitle>
              <DialogDescription className="text-[13.5px] leading-relaxed pt-1">
                The Watching list is a <strong>Buyer Pass</strong> feature. Get
                instant alerts for new litters and price drops from everything
                you watch — plus unlimited inquiries and health-file access.
              </DialogDescription>
            </DialogHeader>
            <div className="pt-2">
              {isNativeApp() ? (
                <IapUpgradeButton
                  packageIds={["buyer_pass_monthly", "buyer_pass_annual"]}
                  label="Unlock with Buyer Pass"
                  context="watchlist_locked"
                  fallbackTo="/billing"
                  fallbackLabel="See plans"
                  className="w-full"
                />
              ) : (
                <Link
                  to="/billing"
                  className="inline-flex w-full items-center justify-center rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white h-11 px-6 text-[14px] font-semibold transition-colors"
                  data-testid="watch-paywall-upgrade"
                >
                  Get Buyer Pass
                </Link>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      className={`${base} ${activeStyle} ${className}`}
      title={watching ? "Stop watching" : "Add to Watching list"}
      data-testid={`watch-btn-${targetType}-${targetId}`}
      data-watching={watching ? "true" : "false"}
    >
      <Heart size={size} weight={watching ? "fill" : "duotone"} />
    </button>
  );
}
